// ExportMenu — dropdown for picking export format (CSV / XLSX / PDF).
//
// The XLSX and PDF builders are dynamically imported the first time the
// user picks them, so the ~500KB of combined library code never lands
// in the main bundle unless the user actually wants those formats.
// CSV is sync (no library needed) so it's always available immediately.

import { useState, useMemo } from 'react';
import { FileDown, ChevronDown, FileText, FileSpreadsheet, FileType2, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { cn, formatIDR } from '@/lib/format';
import { isChunkLoadError } from '@/lib/lazyChunk';
import { downloadBlob } from '@/lib/api/invoice';
import {
  buildEventReportCsv,
  buildEventReportXlsx,
  buildEventReportPdf,
  type PdfCompanyInfo,
  type PdfTransaction,
} from '@/lib/api/reports';
import { useCompanyInfo } from '@/hooks/useSettings';
import { useTransactionsByEvent } from '@/hooks/useTransactions';
import type { EventSummary } from '@/lib/api/reports';

export type ExportFormat = 'csv' | 'xlsx' | 'pdf';

export interface ExportMenuProps {
  event: { id: string; name: string; code: string; startAt?: number; endAt?: number };
  summary: EventSummary;
  disabled?: boolean;
}

interface FormatOption {
  format: ExportFormat;
  label: string;
  description: string;
  icon: typeof FileText;
  /** Loader that returns a Blob for the given format. */
  build: () => Promise<Blob>;
  /** MIME type for the download. */
  mime: string;
  /** Suggested filename (without extension). */
  basename: string;
}

export function ExportMenu({ event, summary, disabled }: ExportMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [busyFormat, setBusyFormat] = useState<ExportFormat | null>(null);
  const [error, setError] = useState<string | null>(null);
  // A chunk the deploy renamed: only a reload can fetch the new one.
  const [staleBuild, setStaleBuild] = useState(false);

  // Pull company branding for the PDF header. We use what's in the
  // cache if available; the PDF builder falls back to "OPHTH POS" so
  // an empty cache doesn't break the export.
  const { data: companyDoc } = useCompanyInfo();
  const company: PdfCompanyInfo = {
    name: companyDoc?.name || 'OPHTH POS',
    address: companyDoc?.address,
    phone: companyDoc?.phone,
    email: companyDoc?.email,
  };

  // Pull the full transaction list for this event. We use it in PDF
  // (Daftar Transaksi section) and XLSX (Transactions sheet). The
  // query is only fired when this component is mounted (i.e. when the
  // Reports page is open), so the read cost is one query per page
  // visit.
  const { data: txList } = useTransactionsByEvent(event.id);
  const transactions: PdfTransaction[] = useMemo(
    () =>
      (txList ?? [])
        .filter((t) => t.paymentStatus !== 'cancelled')
        .map((t) => ({
          invoiceNumber: t.invoiceNumber,
          createdAt: t.createdAt,
          customerName: t.customerSnapshot?.name,
          cashierName: t.cashierName,
          paymentMethod: t.paymentMethod,
          paymentStatus: t.paymentStatus,
          itemCount: t.items.reduce((sum, i) => sum + i.qty, 0),
          grandTotal: t.grandTotal,
          // Receivables — surfaced in the PDF's "Sisa piutang" column
          // and "TEMPO" badge for tempo transactions.
          paymentTerms: t.paymentTerms,
          outstandingAmount: t.outstandingAmount,
          dueDate: t.dueDate,
        })),
    [txList],
  );

  // Filename prefix — kebab-case from event code, plus a timestamp
  const ts = new Date().toISOString().slice(0, 10);
  const basename = `ophth-report-${event.code}-${ts}`;

  // Build all 3 options up front so the menu shows them; the heavy
  // library code is only loaded when the user actually picks the format.
  const options: FormatOption[] = [
    {
      format: 'csv',
      label: 'CSV',
      description: 'Comma-separated values — buka di Excel / Google Sheets.',
      icon: FileText,
      build: async () =>
        new Blob([buildEventReportCsv(event, summary)], {
          type: 'text/csv;charset=utf-8',
        }),
      mime: 'text/csv',
      basename,
    },
    {
      format: 'xlsx',
      label: 'XLSX',
      description:
        transactions.length > 0
          ? `Excel native (${Object.keys({ Summary: 1, 'Payment Breakdown': 1, 'Top Products': 1, Cashiers: 1, Transactions: 1 }).length} sheet, termasuk daftar transaksi).`
          : 'Excel native (multi-sheet).',
      icon: FileSpreadsheet,
      build: async () => buildEventReportXlsx(event, summary, transactions),
      mime: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      basename,
    },
    {
      format: 'pdf',
      description: transactions.length > 0
        ? `Polished PDF report (${transactions.length} transaksi terdaftar).`
        : 'Polished PDF report dengan branding perusahaan.',
      label: 'PDF',
      icon: FileType2,
      build: async () => buildEventReportPdf(event, summary, company, transactions),
      mime: 'application/pdf',
      basename,
    },
  ];

  const handleExport = async (opt: FormatOption) => {
    if (busyFormat) return;
    setError(null);
    setStaleBuild(false);
    setBusyFormat(opt.format);
    setIsOpen(false);
    try {
      const blob = await opt.build();
      const ext = opt.format;
      downloadBlob(blob, `${opt.basename}.${ext}`);
    } catch (e) {
      setStaleBuild(isChunkLoadError(e));
      setError((e as Error).message || `Gagal export ${opt.label}`);
    } finally {
      setBusyFormat(null);
    }
  };

  return (
    <div className="relative">
      <Button
        variant="secondary"
        size="sm"
        onClick={() => !disabled && setIsOpen((o) => !o)}
        disabled={disabled}
        leftIcon={
          busyFormat ? (
            <Loader2 size={14} className="animate-spin" />
          ) : (
            <FileDown size={14} />
          )
        }
        rightIcon={<ChevronDown size={12} />}
        loading={!!busyFormat}
        aria-haspopup="menu"
        aria-expanded={isOpen}
      >
        {busyFormat
          ? `Export ${busyFormat.toUpperCase()}…`
          : 'Export'}
      </Button>

      {isOpen && (
        <>
          <button
            type="button"
            className="fixed inset-0 z-30 cursor-default"
            onClick={() => setIsOpen(false)}
            aria-label="Tutup menu"
          />
          <div
            role="menu"
            className="absolute right-0 mt-2 w-72 z-40 bg-surface border border-line rounded-lg shadow-hover py-1.5 overflow-hidden"
          >
            <div className="px-3 pt-1.5 pb-1.5 text-overline uppercase text-ink-subtle font-medium tracking-wider border-b border-line">
              Format
            </div>
            {options.map((opt) => (
              <button
                key={opt.format}
                type="button"
                role="menuitem"
                onClick={() => handleExport(opt)}
                disabled={!!busyFormat}
                className={cn(
                  'w-full px-3 py-2.5 flex items-start gap-3 text-left hover:bg-canvas/70 transition-colors',
                  'disabled:opacity-50 disabled:cursor-not-allowed',
                )}
              >
                <span className="w-7 h-7 rounded bg-primary/10 text-primary flex items-center justify-center shrink-0 mt-0.5">
                  <opt.icon size={14} />
                </span>
                <span className="flex-1 min-w-0">
                  <span className="flex items-center gap-2">
                    <span className="text-small font-medium text-ink">{opt.label}</span>
                    {busyFormat === opt.format && (
                      <Loader2 size={12} className="animate-spin text-primary" />
                    )}
                  </span>
                  <span className="block text-caption text-ink-subtle mt-0.5">
                    {opt.description}
                  </span>
                </span>
              </button>
            ))}
          </div>
        </>
      )}

      {error && (
        <div className="absolute right-0 mt-2 w-72 z-40 p-2.5 rounded border border-danger/30 bg-danger/5 text-caption text-danger">
          {error}
          {staleBuild && (
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="mt-2 inline-flex items-center gap-1.5 rounded border border-danger/40 px-2.5 py-1.5 font-medium hover:bg-danger/10"
            >
              <RefreshCw size={12} />
              Muat ulang halaman
            </button>
          )}
        </div>
      )}

      {/* Hidden summary so screen readers / search bots see what's
          in the report even though the table is dynamically built. */}
      <span className="sr-only">
        Report summary: {summary.totalTransactions} transaksi, total{' '}
        {formatIDR(summary.totalSales)}.
      </span>
    </div>
  );
}
